import { useEffect, useState } from 'react'
import cx from 'classnames'
import { xor } from 'libraries/lodash'
import { catchError, NEVER, switchMap } from 'rxjs'
import debug from 'libraries/front-end-core/core/debug'
import { toast } from 'react-toastify'
import ClockIcon from '@mui/icons-material/AccessTime'
import ErrorIcon from '@mui/icons-material/Error'
import CheckIcon from '@mui/icons-material/CheckCircle'
import notificationToast from 'helpers/notificationToast'
import { CaEmailDays, SharedEmailPreferences, User } from 'types/User'
import SelectAreaOfLawEmails from './SelectAreaOfLawEmails'
import SelectNoNewsOptIn from './SelectNoNewsOptIn'
import SelectSharedEmailDays from './SelectSharedEmailDays'
import ControlButtons from './ControlButtons'
import { updateSharedEmailPreferences } from 'services/SessionService'
import { refresh } from 'services/ApiService'
import { EMAIL_PREFERENCES } from 'constants/requests'

type EmailPreferencesFormProps = Pick<User, 'accessRoles'> & {
  preferences: SharedEmailPreferences
}

export default function EmailPreferencesForm ({ accessRoles, preferences }: EmailPreferencesFormProps): JSX.Element {
  const [workingPreferences, setWorkingPreferences] = useState(preferences)
  const [changed, setChanged] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    setChanged(false)
    setSubmitted(false)
  }, [submitted])

  const handleChangeDays = (value: number): void => {
    setWorkingPreferences(currentPrefs => ({
      ...currentPrefs,
      caEmailDays: xor(currentPrefs.caEmailDays, [value]) as CaEmailDays
    }))
    setChanged(true)
  }

  const handleToggleAreaOfLaw = (areaOfLaw: SharedEmailPreferences['areaOfLawPrefs'][number]['areaOfLaw'], value: boolean): void => {
    setWorkingPreferences(currentPrefs => ({
      ...currentPrefs,
      areaOfLawPrefs: currentPrefs.areaOfLawPrefs.map(pref =>
        pref.areaOfLaw === areaOfLaw ? { ...pref, enabled: value } : pref
      )
    }))
    setChanged(true)
  }

  const handleToggleNoNews = (value: boolean): void => {
    setWorkingPreferences(currentPrefs => ({
      ...currentPrefs,
      receiveNoNews: value
    }))
    setChanged(true)
  }

  const handleSave = (): void => {
    const notificationId = notificationToast(<ClockIcon />, toast.TYPE.INFO, 'Saving', '')

    setSubmitted(true)

    updateSharedEmailPreferences(workingPreferences)
      .pipe(
        catchError(e => {
          debug.warn(e)
          toast.dismiss(notificationId)
          notificationToast(<ErrorIcon />, toast.TYPE.ERROR, 'Please try again.', 'bg-indian-red white')
          return NEVER
        }),
        switchMap(() => refresh(EMAIL_PREFERENCES))
      )
      .subscribe(() => {
        toast.dismiss(notificationId)
        notificationToast(<CheckIcon />, toast.TYPE.SUCCESS, 'Preferences saved', 'bg-dark-sea-green white')
      })
  }

  const handleCancel = (): void => {
    setWorkingPreferences({ ...preferences })
    setChanged(false)
    setSubmitted(false)
  }

  const { caEmailDays = [], areaOfLawPrefs, receiveNoNews } = workingPreferences

  const visibleAreaOfLawPrefs = areaOfLawPrefs.filter(({ areaOfLaw }) =>
    accessRoles.some(accessRole => accessRole.match(`${areaOfLaw}_news`))
  )

  return (
    <form className={cx('flex flex-column f5 lighter', { 'mb10 pb4': !changed })}>
      <div className='account-item-rows'>
        <SelectSharedEmailDays {...{ caEmailDays, handleChange: handleChangeDays }} />
        <SelectNoNewsOptIn receiveNoNews={receiveNoNews} handleChange={handleToggleNoNews} />
        {
          visibleAreaOfLawPrefs.length > 0 && (
            <SelectAreaOfLawEmails
              areaOfLawPrefs={visibleAreaOfLawPrefs}
              handleChange={handleToggleAreaOfLaw}
            />
          )
        }
      </div>
      <ControlButtons {...{ changed, handleSave, handleCancel }} />
    </form>
  )
}
